import React, { useState, useEffect } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  ScrollView,
  RefreshControl,
  StatusBar,
  Alert,
  Modal,
  FlatList,
  ActivityIndicator,
  TextInput,
  Switch,
} from 'react-native';
import {
  MaterialCommunityIcons,
} from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

// ===== API Configuration =====
const API_BASE_URL = 'https://api.jobeng.ai';

const getCompanyId = async () => {
  try {
    const userData = await AsyncStorage.getItem('currentUser');
    if (userData) {
      const user = JSON.parse(userData);
      return user.company_id || user.companyId || null;
    }
    const companyProfile = await AsyncStorage.getItem('companyProfile');
    if (companyProfile) {
      const company = JSON.parse(companyProfile);
      return company.id || company.company_id || null;
    }
    return null;
  } catch (e) {
    console.error('Error getting company_id:', e);
    return null;
  }
};

const apiRequest = async (endpoint, options = {}) => {
  try {
    const companyId = await getCompanyId();
    // Split endpoint and query string
    const [path, queryString] = endpoint.split('?');
    // Route: auth/* goes to /api/auth/*, everything else to /api/data/{table}
    let baseUrl;
    if (path.startsWith('auth/')) {
      baseUrl = `${API_BASE_URL}/api/${path}`;
    } else {
      // Convert hyphens to underscores for DB table mapping
      const dbPath = path.replace(/-/g, '_');
      baseUrl = `${API_BASE_URL}/api/data/${dbPath}`;
    }
    const params = new URLSearchParams(queryString || '');
    if (companyId) params.set('company_id', companyId);
    const separator = params.toString() ? '?' : '';
    const url = `${baseUrl}${separator}${params.toString()}`;

    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
      ...options,
      ...(options.body ? { body: JSON.stringify(options.body) } : {}),
    });

    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`API request failed for ${endpoint}:`, error);
    throw error;
  }
};
// ===== End API Configuration =====


const { width, height } = Dimensions.get('window');

export default function MobileSalesScreen({ navigation }) {
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('tools');
  const [showToolModal, setShowToolModal] = useState(false);
  const [selectedTool, setSelectedTool] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');

  // Mobile sales tools
  const [mobileTools, setMobileTools] = useState([
    {
      id: 'tool-001',
      name: 'Mobile CRM',
      description: 'Access customer data and manage relationships on the go',
      category: 'crm',
      icon: 'account-group',
      color: '#3B82F6',
      features: ['Contact Management', 'Lead Tracking', 'Activity Logging', 'Offline Access'],
      usage: 'daily',
      lastUsed: '2024-01-20T14:30:00Z',
      rating: 4.8,
      downloads: 1247,
      status: 'active',
      version: '2.1.0',
      size: '45.2 MB',
      permissions: ['Contacts', 'Location', 'Camera', 'Storage']
    },
    {
      id: 'tool-002',
      name: 'Quote Builder',
      description: 'Create professional quotes and proposals instantly',
      category: 'quotes',
      icon: 'file-document-edit',
      color: '#10B981',
      features: ['Template Library', 'Digital Signatures', 'PDF Export', 'Price Calculator'],
      usage: 'weekly',
      lastUsed: '2024-01-19T10:15:00Z',
      rating: 4.6,
      downloads: 856,
      status: 'active',
      version: '1.8.3',
      size: '32.1 MB',
      permissions: ['Storage', 'Camera', 'Microphone']
    },
    {
      id: 'tool-003',
      name: 'Field Sales Tracker',
      description: 'Track visits, routes, and field activities with GPS',
      category: 'tracking',
      icon: 'map-marker-path',
      color: '#F59E0B',
      features: ['GPS Tracking', 'Visit Logging', 'Route Optimization', 'Expense Tracking'],
      usage: 'daily',
      lastUsed: '2024-01-20T16:45:00Z',
      rating: 4.4,
      downloads: 2341,
      status: 'active',
      version: '3.2.1',
      size: '67.8 MB',
      permissions: ['Location', 'Camera', 'Storage', 'Phone']
    },
    {
      id: 'tool-004',
      name: 'Inventory Scanner',
      description: 'Scan barcodes and manage inventory in real-time',
      category: 'inventory',
      icon: 'barcode-scan',
      color: '#8B5CF6',
      features: ['Barcode Scanning', 'Stock Levels', 'Order Management', 'Sync with ERP'],
      usage: 'weekly',
      lastUsed: '2024-01-18T12:20:00Z',
      rating: 4.2,
      downloads: 567,
      status: 'active',
      version: '1.5.7',
      size: '28.9 MB',
      permissions: ['Camera', 'Storage', 'Network']
    },
    {
      id: 'tool-005',
      name: 'Sales Presentation',
      description: 'Interactive presentations and product demos',
      category: 'presentation',
      icon: 'presentation',
      color: '#EF4444',
      features: ['Interactive Slides', 'Product Catalog', 'Video Demos', 'Customer Feedback'],
      usage: 'monthly',
      lastUsed: '2024-01-15T14:00:00Z',
      rating: 4.7,
      downloads: 1893,
      status: 'active',
      version: '2.0.5',
      size: '156.3 MB',
      permissions: ['Storage', 'Camera', 'Microphone', 'Network']
    },
    {
      id: 'tool-006',
      name: 'Expense Manager',
      description: 'Track and submit expenses with receipt capture',
      category: 'expenses',
      icon: 'receipt',
      color: '#06B6D4',
      features: ['Receipt Scanning', 'Expense Categories', 'Mileage Tracking', 'Report Generation'],
      usage: 'weekly',
      lastUsed: '2024-01-17T09:30:00Z',
      rating: 4.3,
      downloads: 734,
      status: 'active',
      version: '1.9.2',
      size: '41.7 MB',
      permissions: ['Camera', 'Location', 'Storage']
    }
  ]);

  // Mobile features
  const [mobileFeatures, setMobileFeatures] = useState([
    {
      id: 'feature-001',
      name: 'Offline Mode',
      description: 'Work without internet connection and sync when online',
      icon: 'wifi-off',
      color: '#3B82F6',
      enabled: true,
      category: 'connectivity',
      benefits: ['Work anywhere', 'No data loss', 'Auto sync', 'Improved reliability']
    },
    {
      id: 'feature-002',
      name: 'Voice Commands',
      description: 'Control the app using voice commands for hands-free operation',
      icon: 'microphone',
      color: '#10B981',
      enabled: false,
      category: 'accessibility',
      benefits: ['Hands-free operation', 'Faster data entry', 'Accessibility', 'Multitasking']
    },
    {
      id: 'feature-003',
      name: 'Biometric Security',
      description: 'Secure app access with fingerprint or face recognition',
      icon: 'fingerprint',
      color: '#F59E0B',
      enabled: true,
      category: 'security',
      benefits: ['Enhanced security', 'Quick access', 'No passwords', 'Privacy protection']
    },
    {
      id: 'feature-004',
      name: 'Push Notifications',
      description: 'Real-time alerts for important sales activities and updates',
      icon: 'bell',
      color: '#8B5CF6',
      enabled: true,
      category: 'notifications',
      benefits: ['Real-time updates', 'Never miss opportunities', 'Customizable', 'Priority alerts']
    },
    {
      id: 'feature-005',
      name: 'Dark Mode',
      description: 'Switch to dark theme for better viewing in low light',
      icon: 'weather-night',
      color: '#6B7280',
      enabled: false,
      category: 'display',
      benefits: ['Eye comfort', 'Battery saving', 'Professional look', 'Low light viewing']
    },
    {
      id: 'feature-006',
      name: 'Gesture Controls',
      description: 'Navigate the app using intuitive touch gestures',
      icon: 'gesture-swipe',
      color: '#EF4444',
      enabled: true,
      category: 'navigation',
      benefits: ['Intuitive navigation', 'Faster access', 'One-handed use', 'Modern interface']
    }
  ]);

  // Mobile analytics
  const [mobileAnalytics, setMobileAnalytics] = useState({
    totalUsers: 1247,
    activeUsers: 856,
    dailyActiveUsers: 234,
    avgSessionTime: 12.5, // minutes
    totalSessions: 5678,
    crashRate: 0.02, // percentage
    appRating: 4.6,
    downloadGrowth: 15.8, // percentage
    retentionRate: 78.3, // percentage
    topFeature: 'Mobile CRM',
    mostUsedTool: 'Field Sales Tracker',
    avgToolsPerUser: 3.2
  });

  // Usage statistics
  const [usageStats, setUsageStats] = useState([
    {
      id: 'stat-001',
      toolName: 'Mobile CRM',
      category: 'crm',
      dailyUsers: 234,
      weeklyUsers: 567,
      monthlyUsers: 856,
      avgSessionTime: 15.2,
      totalSessions: 2341,
      rating: 4.8,
      growth: 12.3
    },
    {
      id: 'stat-002',
      toolName: 'Field Sales Tracker',
      category: 'tracking',
      dailyUsers: 189,
      weeklyUsers: 445,
      monthlyUsers: 734,
      avgSessionTime: 22.7,
      totalSessions: 1876,
      rating: 4.4,
      growth: 18.9
    },
    {
      id: 'stat-003',
      toolName: 'Quote Builder',
      category: 'quotes',
      dailyUsers: 156,
      weeklyUsers: 389,
      monthlyUsers: 623,
      avgSessionTime: 8.9,
      totalSessions: 1234,
      rating: 4.6,
      growth: 8.7
    },
    {
      id: 'stat-004',
      toolName: 'Sales Presentation',
      category: 'presentation',
      dailyUsers: 98,
      weeklyUsers: 267,
      monthlyUsers: 445,
      avgSessionTime: 18.4,
      totalSessions: 987,
      rating: 4.7,
      growth: 25.6
    },
    {
      id: 'stat-005',
      toolName: 'Inventory Scanner',
      category: 'inventory',
      dailyUsers: 67,
      weeklyUsers: 178,
      monthlyUsers: 289,
      avgSessionTime: 6.3,
      totalSessions: 567,
      rating: 4.2,
      growth: 5.4
    },
    {
      id: 'stat-006',
      toolName: 'Expense Manager',
      category: 'expenses',
      dailyUsers: 45,
      weeklyUsers: 123,
      monthlyUsers: 234,
      avgSessionTime: 4.8,
      totalSessions: 345,
      rating: 4.3,
      growth: 14.2
    }
  ]);

  // Device compatibility
  const [deviceCompatibility, setDeviceCompatibility] = useState([
    {
      platform: 'iOS',
      minVersion: '13.0',
      supportedDevices: ['iPhone 8+', 'iPad Air 2+', 'iPad Pro'],
      compatibility: 98.5,
      users: 567,
      icon: 'apple-ios'
    },
    {
      platform: 'Android',
      minVersion: '8.0',
      supportedDevices: ['Android 8.0+', 'Tablets 10"+'],
      compatibility: 94.2,
      users: 680,
      icon: 'android'
    },
    {
      platform: 'Web App',
      minVersion: 'PWA',
      supportedDevices: ['Chrome 80+', 'Safari 13+', 'Edge 80+'],
      compatibility: 89.7,
      users: 234,
      icon: 'web'
    }
  ]);

  useEffect(() => {
    initializeScreen();
  }, []);

  const initializeScreen = async () => {
    try {
      setIsLoading(true);
      // Simulate loading mobile sales data
      await new Promise(resolve => setTimeout(resolve, 1000));
    } catch (error) {
      console.error('Error initializing screen:', error);
      Alert.alert('Error', 'Failed to load mobile sales data');
    } finally {
      setIsLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setRefreshing(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      'crm': '#3B82F6',
      'quotes': '#10B981',
      'tracking': '#F59E0B',
      'inventory': '#8B5CF6',
      'presentation': '#EF4444',
      'expenses': '#06B6D4',
      'connectivity': '#3B82F6',
      'accessibility': '#10B981',
      'security': '#F59E0B',
      'notifications': '#8B5CF6',
      'display': '#6B7280',
      'navigation': '#EF4444'
    };
    return colors[category] || '#6B7280';
  };

  const getUsageColor = (usage) => {
    const colors = {
      'daily': '#10B981',
      'weekly': '#3B82F6',
      'monthly': '#F59E0B',
      'rarely': '#6B7280'
    };
    return colors[usage] || '#6B7280';
  };

  const filteredTools = mobileTools.filter(tool => {
    const matchesSearch = String(tool.name ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         String(tool.description ?? '').toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = filterCategory === 'all' || tool.category === filterCategory;
    
    return matchesSearch && matchesFilter;
  });

  const toggleFeature = (featureId) => {
    setMobileFeatures(mobileFeatures.map(feature => 
      feature.id === featureId 
        ? { ...feature, enabled: !feature.enabled }
        : feature
    ));
  };

  const handleInstallTool = (tool) => {
    Alert.alert(
      'Install Tool',
      `Would you like to install ${tool.name}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Install',
          onPress: () => {
            Alert.alert('Success', `${tool.name} installed successfully!`);
          }
        }
      ]
    );
  };

  const renderHeader = () => (
    <LinearGradient
      colors={['#1F2937', '#374151']}
      style={styles.header}
    >
      <View style={styles.headerContent}>
        <TouchableOpacity 
          onPress={() => navigation?.goBack()}
          style={styles.backButton}
        >
          <MaterialCommunityIcons name="arrow-left" size={24} color="#FFFFFF" />
        </TouchableOpacity>
        
        <View style={styles.headerCenter}>
          <Text style={styles.headerTitle}>Mobile Sales</Text>
          <Text style={styles.headerSubtitle}>Sales Tools on the Go</Text>
        </View>
        
        <TouchableOpacity 
          style={styles.headerButton}
          onPress={() => Alert.alert('Download', 'Mobile app download links would be provided here')}
        >
          <MaterialCommunityIcons name="download" size={24} color="#FFFFFF" />
        </TouchableOpacity>
      </View>
    </LinearGradient>
  );

  const renderTabs = () => (
    <View style={styles.tabsContainer}>
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.tabsContent}
      >
        {[
          { key: 'tools', title: 'Tools', icon: 'toolbox' },
          { key: 'features', title: 'Features', icon: 'feature-search' },
          { key: 'analytics', title: 'Analytics', icon: 'chart-line' },
          { key: 'compatibility', title: 'Devices', icon: 'devices' }
        ].map((tab) => (
          <TouchableOpacity
            key={tab.key}
            style={[
              styles.tab,
              activeTab === tab.key && styles.activeTab
            ]}
            onPress={() => setActiveTab(tab.key)}
          >
            <MaterialCommunityIcons 
              name={tab.icon} 
              size={20} 
              color={activeTab === tab.key ? '#3B82F6' : '#6B7280'} 
            />
            <Text style={[
              styles.tabText,
              activeTab === tab.key && styles.activeTabText
            ]}>
              {tab.title}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderSearchAndFilter = () => (
    <View style={styles.searchContainer}>
      <View style={styles.searchBar}>
        <MaterialCommunityIcons name="magnify" size={20} color="#6B7280" />
        <TextInput
          style={styles.searchInput}
          placeholder="Search mobile tools..."
          value={searchQuery}
          onChangeText={setSearchQuery}
          placeholderTextColor="#9CA3AF"
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <MaterialCommunityIcons name="close" size={20} color="#6B7280" />
          </TouchableOpacity>
        )}
      </View>
      
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false}
        style={styles.filterContainer}
      >
        {[
          { key: 'all', label: 'All' },
          { key: 'crm', label: 'CRM' },
          { key: 'quotes', label: 'Quotes' },
          { key: 'tracking', label: 'Tracking' },
          { key: 'inventory', label: 'Inventory' },
          { key: 'presentation', label: 'Presentation' },
          { key: 'expenses', label: 'Expenses' }
        ].map((filter) => (
          <TouchableOpacity
            key={filter.key}
            style={[
              styles.filterButton,
              filterCategory === filter.key && styles.activeFilterButton
            ]}
            onPress={() => setFilterCategory(filter.key)}
          >
            <Text style={[
              styles.filterButtonText,
              filterCategory === filter.key && styles.activeFilterButtonText
            ]}>
              {filter.label}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderMobileStats = () => {
    const stats = [
      {
        title: 'Total Users',
        value: mobileAnalytics.totalUsers.toLocaleString(),
        icon: 'account-group',
        color: '#3B82F6'
      },
      {
        title: 'Active Users',
        value: mobileAnalytics.activeUsers.toLocaleString(),
        icon: 'account-check',
        color: '#10B981'
      },
      {
        title: 'App Rating',
        value: mobileAnalytics.appRating.toString(),
        icon: 'star',
        color: '#F59E0B'
      },
      {
        title: 'Retention Rate',
        value: `${mobileAnalytics.retentionRate}%`,
        icon: 'account-heart',
        color: '#8B5CF6'
      }
    ];

  
  // Load sales data from API on mount
  useEffect(() => {
    const loadFromApi = async () => {
      try {
        const [toolsResp, featuresResp, analyticsResp, statsResp] = await Promise.all([
          apiRequest('mobile-sales-tools'),
          apiRequest('mobile-sales-features'),
          apiRequest('mobile-sales-analytics'),
          apiRequest('mobile-sales-usage-stats'),
        ]);
        if (toolsResp.success && toolsResp.data) setMobileTools(toolsResp.data);
        if (featuresResp.success && featuresResp.data) setMobileFeatures(featuresResp.data);
        if (analyticsResp.success && analyticsResp.data) setMobileAnalytics(analyticsResp.data);
        if (statsResp.success && statsResp.data) setUsageStats(statsResp.data);
      } catch (error) {
        console.warn('API unavailable, using local data:', error.message);
      } finally {
        setIsLoading(false);
      }
    };
    loadFromApi();
  }, []);

  // Save tool/feature updates via API
  const saveToApi = async (endpoint, data) => {
    try {
      await apiRequest(endpoint, { method: 'PUT', body: data });
    } catch (error) {
      console.warn('API save failed:', error.message);
    }
  };

  return (
      <FlatList
        data={stats}
        keyExtractor={(item, index) => index.toString()}
        renderItem={({ item }) => (
          <View style={styles.statCard}>
            <View style={[styles.statIcon, { backgroundColor: item.color }]}>
              <MaterialCommunityIcons name={item.icon} size={24} color="#FFFFFF" />
            </View>
            <Text style={styles.statValue}>{item.value}</Text>
            <Text style={styles.statTitle}>{item.title}</Text>
          </View>
        )}
        numColumns={2}
        columnWrapperStyle={styles.row}
        contentContainerStyle={styles.statsContainer}
        scrollEnabled={false}
      />
    );
  };

  const renderMobileTools = () => (
    <FlatList
      data={filteredTools}
      keyExtractor={(item) => item.id}
      renderItem={({ item }) => (
        <TouchableOpacity 
          style={styles.toolCard}
          onPress={() => {
            setSelectedTool(item);
            setShowToolModal(true);
          }}
        >
          <View style={styles.toolHeader}>
            <View style={styles.toolInfo}>
              <View style={styles.toolTitleRow}>
                <View style={[styles.toolIcon, { backgroundColor: item.color }]}>
                  <MaterialCommunityIcons name={item.icon} size={20} color="#FFFFFF" />
                </View>
                <View style={styles.toolTitleInfo}>
                  <Text style={styles.toolTitle}>{item.name}</Text>
                  <Text style={styles.toolVersion}>v{item.version}</Text>
                </View>
              </View>
              <Text style={styles.toolDescription}>{item.description}</Text>
            </View>
            <View style={styles.toolMeta}>
              <View style={[styles.usageBadge, { backgroundColor: getUsageColor(item.usage) }]}>
                <Text style={styles.usageText}>{item.usage}</Text>
              </View>
              <View style={styles.rating}>
                <MaterialCommunityIcons name="star" size={14} color="#F59E0B" />
                <Text style={styles.ratingText}>{item.rating}</Text>
              </View>
            </View>
          </View>
          
          <View style={styles.toolDetails}>
            <View style={styles.toolDetail}>
              <Text style={styles.toolDetailLabel}>Size</Text>
              <Text style={styles.toolDetailValue}>{item.size}</Text>
            </View>
            <View style={styles.toolDetail}>
              <Text style={styles.toolDetailLabel}>Downloads</Text>
              <Text style={styles.toolDetailValue}>{item.downloads.toLocaleString()}</Text>
            </View>
            <View style={styles.toolDetail}>
              <Text style={styles.toolDetailLabel}>Last Used</Text>
              <Text style={styles.toolDetailValue}>{formatDate(item.lastUsed)}</Text>
            </View>
          </View>
          
          <View style={styles.toolFeatures}>
            <Text style={styles.featuresTitle}>Key Features:</Text>
            <View style={styles.featuresList}>
              {item.features.slice(0, 2).map((feature, index) => (
                <Text key={index} style={styles.featureText}>• {feature}</Text>
              ))}
              {item.features.length > 2 && (
                <Text style={styles.featureText}>• +{item.features.length - 2} more</Text>
              )}
            </View>
          </View>
          
          <View style={styles.toolFooter}>
            <TouchableOpacity 
              style={styles.installButton}
              onPress={() => handleInstallTool(item)}
            >
              <MaterialCommunityIcons name="download" size={16} color="#FFFFFF" />
              <Text style={styles.installButtonText}>Install</Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      )}
      numColumns={2}
      columnWrapperStyle={styles.row}
      contentContainerStyle={styles.listContainer}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={onRefresh}
          colors={['#3B82F6']}
        />
      }
      showsVerticalScrollIndicator={false}
    />
  );

  const renderMobileFeatures = () => (
    <FlatList
      data={mobileFeatures}
      keyExtractor={(item) => item.id}
      renderItem={({ item }) => (
        <View style={styles.featureCard}>
          <View style={styles.featureHeader}>
            <View style={styles.featureInfo}>
              <View style={styles.featureTitleRow}>
                <View style={[styles.featureIcon, { backgroundColor: item.color }]}>
                  <MaterialCommunityIcons name={item.icon} size={20} color="#FFFFFF" />
                </View>
                <View style={styles.featureTitleInfo}>
                  <Text style={styles.featureTitle}>{item.name}</Text>
                  <Text style={styles.featureCategory}>{item.category}</Text>
                </View>
              </View>
              <Text style={styles.featureDescription}>{item.description}</Text>
            </View>
            <Switch
              value={item.enabled}
              onValueChange={() => toggleFeature(item.id)}
              trackColor={{ false: '#D1D5DB', true: '#10B981' }}
              thumbColor={item.enabled ? '#FFFFFF' : '#F3F4F6'}
            />
          </View>
          
          <View style={styles.featureBenefits}>
            <Text style={styles.benefitsTitle}>Benefits:</Text>
            <View style={styles.benefitsList}>
              {item.benefits.map((benefit, index) => (
                <View key={index} style={styles.benefitItem}>
                  <MaterialCommunityIcons name="check-circle" size={12} color="#10B981" />
                  <Text style={styles.benefitText}>{benefit}</Text>
                </View>
              ))}
            </View>
          </View>
        </View>
      )}
      contentContainerStyle={styles.listContainer}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={onRefresh}
          colors={['#3B82F6']}
        />
      }
      showsVerticalScrollIndicator={false}
    />
  );

  const renderAnalytics = () => (
    <ScrollView 
      style={styles.contentContainer}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={onRefresh}
          colors={['#3B82F6']}
        />
      }
      showsVerticalScrollIndicator={false}
    >
      {renderMobileStats()}
      
      <View style={styles.analyticsSection}>
        <Text style={styles.sectionTitle}>Usage Statistics</Text>
        <View style={styles.usageMetrics}>
          <View style={styles.usageItem}>
            <Text style={styles.usageLabel}>Daily Active Users</Text>
            <Text style={styles.usageValue}>{mobileAnalytics.dailyActiveUsers}</Text>
          </View>
          <View style={styles.usageItem}>
            <Text style={styles.usageLabel}>Avg Session Time</Text>
            <Text style={styles.usageValue}>{mobileAnalytics.avgSessionTime} min</Text>
          </View>
          <View style={styles.usageItem}>
            <Text style={styles.usageLabel}>Total Sessions</Text>
            <Text style={styles.usageValue}>{mobileAnalytics.totalSessions.toLocaleString()}</Text>
          </View>
          <View style={styles.usageItem}>
            <Text style={styles.usageLabel}>Crash Rate</Text>
            <Text style={[styles.usageValue, { color: '#EF4444' }]}>{mobileAnalytics.crashRate}%</Text>
          </View>
        </View>
      </View>
      
      <View style={styles.analyticsSection}>
        <Text style={styles.sectionTitle}>Tool Performance</Text>
        <View style={styles.toolPerformance}>
          {usageStats.slice(0, 4).map((stat, index) => (
            <View key={index} style={styles.performanceItem}>
              <View style={styles.performanceInfo}>
                <Text style={styles.performanceName}>{stat.toolName}</Text>
                <Text style={styles.performanceCategory}>{stat.category}</Text>
              </View>
              <View style={styles.performanceMetrics}>
                <Text style={styles.performanceMetric}>{stat.dailyUsers} daily users</Text>
                <Text style={styles.performanceMetric}>{stat.avgSessionTime} min avg</Text>
                <Text style={[styles.performanceMetric, { color: stat.growth > 0 ? '#10B981' : '#EF4444' }]}>
                  {stat.growth > 0 ? '+' : ''}{stat.growth}% growth
                </Text>
              </View>
            </View>
          ))}
        </View>
      </View>
      
      <View style={styles.analyticsSection}>
        <Text style={styles.sectionTitle}>Performance Insights</Text>
        <View style={styles.performanceInsights}>
          <View style={styles.insightItem}>
            <MaterialCommunityIcons name="trending-up" size={20} color="#10B981" />
            <Text style={styles.insightText}>Mobile CRM is the most popular tool with 234 daily users</Text>
          </View>
          <View style={styles.insightItem}>
            <MaterialCommunityIcons name="clock-fast" size={20} color="#3B82F6" />
            <Text style={styles.insightText}>Average session time increased by 18% this month</Text>
          </View>
          <View style={styles.insightItem}>
            <MaterialCommunityIcons name="star" size={20} color="#F59E0B" />
            <Text style={styles.insightText}>App rating improved to 4.6 stars with 98% satisfaction</Text>
          </View>
          <View style={styles.insightItem}>
            <MaterialCommunityIcons name="shield-check" size={20} color="#10B981" />
            <Text style={styles.insightText}>Crash rate reduced to 0.02% - industry leading performance</Text>
          </View>
        </View>
      </View>
    </ScrollView>
  );

  const renderCompatibility = () => (
    <ScrollView 
      style={styles.contentContainer}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={onRefresh}
          colors={['#3B82F6']}
        />
      }
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.compatibilitySection}>
        <Text style={styles.sectionTitle}>Device Compatibility</Text>
        <View style={styles.platformList}>
          {deviceCompatibility.map((platform, index) => (
            <View key={index} style={styles.platformCard}>
              <View style={styles.platformHeader}>
                <View style={styles.platformInfo}>
                  <MaterialCommunityIcons name={platform.icon} size={32} color="#3B82F6" />
                  <View style={styles.platformDetails}>
                    <Text style={styles.platformName}>{platform.platform}</Text>
                    <Text style={styles.platformVersion}>Min version: {platform.minVersion}</Text>
                  </View>
                </View>
                <View style={styles.platformStats}>
                  <Text style={styles.platformUsers}>{platform.users} users</Text>
                  <Text style={styles.platformCompatibility}>{platform.compatibility}% compatible</Text>
                </View>
              </View>
              
              <View style={styles.platformDevices}>
                <Text style={styles.devicesTitle}>Supported Devices:</Text>
                {platform.supportedDevices.map((device, deviceIndex) => (
                  <Text key={deviceIndex} style={styles.deviceText}>• {device}</Text>
                ))}
              </View>
              
              <View style={styles.compatibilityBar}>
                <View 
                  style={[
                    styles.compatibilityFill, 
                    { 
                      width: `${platform.compatibility}%`,
                      backgroundColor: platform.compatibility > 95 ? '#10B981' : platform.compatibility > 90 ? '#F59E0B' : '#EF4444'
                    }
                  ]} 
                />
              </View>
            </View>
          ))}
        </View>
      </View>
      
      <View style={styles.compatibilitySection}>
        <Text style={styles.sectionTitle}>System Requirements</Text>
        <View style={styles.requirementsList}>
          <View style={styles.requirementItem}>
            <MaterialCommunityIcons name="memory" size={20} color="#3B82F6" />
            <View style={styles.requirementInfo}>
              <Text style={styles.requirementTitle}>RAM</Text>
              <Text style={styles.requirementValue}>Minimum 2GB, Recommended 4GB+</Text>
            </View>
          </View>
          <View style={styles.requirementItem}>
            <MaterialCommunityIcons name="harddisk" size={20} color="#10B981" />
            <View style={styles.requirementInfo}>
              <Text style={styles.requirementTitle}>Storage</Text>
              <Text style={styles.requirementValue}>500MB free space for installation</Text>
            </View>
          </View>
          <View style={styles.requirementItem}>
            <MaterialCommunityIcons name="wifi" size={20} color="#F59E0B" />
            <View style={styles.requirementInfo}>
              <Text style={styles.requirementTitle}>Network</Text>
              <Text style={styles.requirementValue}>Internet connection for sync (offline mode available)</Text>
            </View>
          </View>
          <View style={styles.requirementItem}>
            <MaterialCommunityIcons name="camera" size={20} color="#8B5CF6" />
            <View style={styles.requirementInfo}>
              <Text style={styles.requirementTitle}>Camera</Text>
              <Text style={styles.requirementValue}>Required for barcode scanning and receipt capture</Text>
            </View>
          </View>
          <View style={styles.requirementItem}>
            <MaterialCommunityIcons name="map-marker" size={20} color="#EF4444" />
            <View style={styles.requirementInfo}>
              <Text style={styles.requirementTitle}>Location</Text>
              <Text style={styles.requirementValue}>GPS for field sales tracking and route optimization</Text>
            </View>
          </View>
        </View>
      </View>
      
      <View style={styles.compatibilitySection}>
        <Text style={styles.sectionTitle}>Download Information</Text>
        <View style={styles.downloadInfo}>
          <View style={styles.downloadItem}>
            <MaterialCommunityIcons name="download" size={20} color="#3B82F6" />
            <Text style={styles.downloadText}>Total app size: 45-156 MB depending on tools</Text>
          </View>
          <View style={styles.downloadItem}>
            <MaterialCommunityIcons name="update" size={20} color="#10B981" />
            <Text style={styles.downloadText}>Automatic updates available</Text>
          </View>
          <View style={styles.downloadItem}>
            <MaterialCommunityIcons name="cloud-download" size={20} color="#F59E0B" />
            <Text style={styles.downloadText}>Cloud sync for data backup</Text>
          </View>
          <View style={styles.downloadItem}>
            <MaterialCommunityIcons name="shield-check" size={20} color="#10B981" />
            <Text style={styles.downloadText}>Enterprise security and compliance</Text>
          </View>
        </View>
      </View>
    </ScrollView>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'tools':
        return (
          <View style={styles.contentContainer}>
            {renderSearchAndFilter()}
            {renderMobileTools()}
          </View>
        );
      case 'features':
        return renderMobileFeatures();
      case 'analytics':
        return renderAnalytics();
      case 'compatibility':
        return renderCompatibility();
      default:
        return null;
    }
  };

  const renderToolModal = () => (
    <Modal
      visible={showToolModal}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => {
        setShowToolModal(false);
        setSelectedTool(null);
      }}
    >
      <View style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>
            {selectedTool ? selectedTool.name : 'Tool Details'}
          </Text>
          <TouchableOpacity 
            style={styles.modalCloseButton}
            onPress={() => {
              setShowToolModal(false);
              setSelectedTool(null);
            }}
          >
            <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
          </TouchableOpacity>
        </View>
        
        {selectedTool && (
          <ScrollView style={styles.modalContent}>
            <View style={styles.toolPreview}>
              <View style={styles.toolPreviewHeader}>
                <View style={styles.toolPreviewInfo}>
                  <View style={[styles.toolIcon, { backgroundColor: selectedTool.color }]}>
                    <MaterialCommunityIcons name={selectedTool.icon} size={24} color="#FFFFFF" />
                  </View>
                  <View style={styles.toolPreviewTitleInfo}>
                    <Text style={styles.toolPreviewTitle}>{selectedTool.name}</Text>
                    <Text style={styles.toolPreviewVersion}>Version {selectedTool.version}</Text>
                  </View>
                </View>
                <View style={styles.toolPreviewMeta}>
                  <View style={styles.rating}>
                    <MaterialCommunityIcons name="star" size={16} color="#F59E0B" />
                    <Text style={styles.ratingText}>{selectedTool.rating}</Text>
                  </View>
                  <Text style={styles.downloadCount}>{selectedTool.downloads.toLocaleString()} downloads</Text>
                </View>
              </View>
              
              <View style={styles.toolPreviewSection}>
                <Text style={styles.toolPreviewSectionTitle}>Description</Text>
                <Text style={styles.toolPreviewText}>{selectedTool.description}</Text>
              </View>
              
              <View style={styles.toolPreviewSection}>
                <Text style={styles.toolPreviewSectionTitle}>Features</Text>
                <View style={styles.featuresList}>
                  {selectedTool.features.map((feature, index) => (
                    <View key={index} style={styles.featureItem}>
                      <MaterialCommunityIcons name="check-circle" size={16} color="#10B981" />
                      <Text style={styles.featureItemText}>{feature}</Text>
                    </View>
                  ))}
                </View>
              </View>
              
              <View style={styles.toolPreviewSection}>
                <Text style={styles.toolPreviewSectionTitle}>App Information</Text>
                <View style={styles.appInfo}>
                  <View style={styles.appInfoItem}>
                    <Text style={styles.appInfoLabel}>Size:</Text>
                    <Text style={styles.appInfoValue}>{selectedTool.size}</Text>
                  </View>
                  <View style={styles.appInfoItem}>
                    <Text style={styles.appInfoLabel}>Category:</Text>
                    <Text style={styles.appInfoValue}>{selectedTool.category}</Text>
                  </View>
                  <View style={styles.appInfoItem}>
                    <Text style={styles.appInfoLabel}>Usage:</Text>
                    <Text style={styles.appInfoValue}>{selectedTool.usage}</Text>
                  </View>
                  <View style={styles.appInfoItem}>
                    <Text style={styles.appInfoLabel}>Last Used:</Text>
                    <Text style={styles.appInfoValue}>{formatDate(selectedTool.lastUsed)}</Text>
                  </View>
                </View>
              </View>
              
              <View style={styles.toolPreviewSection}>
                <Text style={styles.toolPreviewSectionTitle}>Permissions</Text>
                <View style={styles.permissionsList}>
                  {selectedTool.permissions.map((permission, index) => (
                    <View key={index} style={styles.permissionItem}>
                      <MaterialCommunityIcons name="shield-check" size={14} color="#3B82F6" />
                      <Text style={styles.permissionText}>{permission}</Text>
                    </View>
                  ))}
                </View>
              </View>
            </View>
          </ScrollView>
        )}
        
        <View style={styles.modalFooter}>
          <TouchableOpacity 
            style={[styles.modalButton, styles.installButton]}
            onPress={() => {
              handleInstallTool(selectedTool);
              setShowToolModal(false);
              setSelectedTool(null);
            }}
          >
            <MaterialCommunityIcons name="download" size={16} color="#FFFFFF" />
            <Text style={styles.installButtonText}>Install Tool</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );

  if (isLoading) {
    return (
      <SafeAreaView style={styles.loadingContainer}>
        <ActivityIndicator size={36} color="#3B82F6" />
        <Text style={styles.loadingText}>Loading Mobile Sales...</Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#1F2937" />
      
      {renderHeader()}
      {renderTabs()}
      {renderContent()}
      {renderToolModal()}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
  },
  
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#6B7280',
    fontWeight: '500',
  },
  
  // Header styles
  header: {
    paddingTop: 12,
    paddingBottom: 16,
    paddingHorizontal: 16,
  },
  
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  headerCenter: {
    flex: 1,
    alignItems: 'center',
  },
  
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  
  headerSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 2,
  },
  
  headerButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  // Tabs styles
  tabsContainer: {
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  
  tabsContent: {
    paddingHorizontal: 16,
  },
  
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginRight: 8,
    borderRadius: 20,
  },
  
  activeTab: {
    backgroundColor: '#EBF4FF',
  },
  
  tabText: {
    fontSize: 14,
    color: '#6B7280',
    marginLeft: 8,
    fontWeight: '500',
  },
  
  activeTabText: {
    color: '#3B82F6',
    fontWeight: '600',
  },
  
  // Content styles
  contentContainer: {
    flex: 1,
  },
  
  listContainer: {
    padding: 16,
  },
  
  // Search and filter styles
  searchContainer: {
    padding: 16,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  
  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F3F4F6',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginBottom: 12,
  },
  
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#1F2937',
    marginLeft: 8,
  },
  
  filterContainer: {
    flexDirection: 'row',
  },
  
  filterButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: '#F3F4F6',
    marginRight: 8,
  },
  
  activeFilterButton: {
    backgroundColor: '#3B82F6',
  },
  
  filterButtonText: {
    fontSize: 14,
    color: '#6B7280',
    fontWeight: '500',
  },
  
  activeFilterButtonText: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  
  // Stats styles
  statsContainer: {
    padding: 16,
  },
  
  row: {
    justifyContent: 'space-between',
  },
  
  statCard: {
    width: (width - 48) / 2,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    alignItems: 'center',
  },
  
  statIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  
  statValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
    marginBottom: 4,
  },
  
  statTitle: {
    fontSize: 12,
    color: '#6B7280',
    textAlign: 'center',
  },
  
  // Tool card styles
  toolCard: {
    width: (width - 48) / 2,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  
  toolHeader: {
    marginBottom: 12,
  },
  
  toolInfo: {
    marginBottom: 8,
  },
  
  toolTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  
  toolIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 8,
  },
  
  toolTitleInfo: {
    flex: 1,
  },
  
  toolTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  toolVersion: {
    fontSize: 11,
    color: '#6B7280',
  },
  
  toolDescription: {
    fontSize: 12,
    color: '#6B7280',
    lineHeight: 16,
  },
  
  toolMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  
  usageBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
  },
  
  usageText: {
    fontSize: 9,
    fontWeight: '600',
    color: '#FFFFFF',
    textTransform: 'capitalize',
  },
  
  rating: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  
  ratingText: {
    fontSize: 11,
    color: '#6B7280',
    marginLeft: 2,
  },
  
  toolDetails: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  
  toolDetail: {
    flex: 1,
  },
  
  toolDetailLabel: {
    fontSize: 10,
    color: '#6B7280',
    marginBottom: 2,
  },
  
  toolDetailValue: {
    fontSize: 11,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  toolFeatures: {
    marginBottom: 12,
  },
  
  featuresTitle: {
    fontSize: 11,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 4,
  },
  
  featuresList: {
    flexDirection: 'column',
  },
  
  featureText: {
    fontSize: 10,
    color: '#6B7280',
    marginBottom: 2,
  },
  
  toolFooter: {
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    paddingTop: 8,
  },
  
  installButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#3B82F6',
    paddingVertical: 8,
    borderRadius: 6,
  },
  
  installButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 4,
  },
  
  // Feature card styles
  featureCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  
  featureHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  
  featureInfo: {
    flex: 1,
  },
  
  featureTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  
  featureIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  
  featureTitleInfo: {
    flex: 1,
  },
  
  featureTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  featureCategory: {
    fontSize: 12,
    color: '#6B7280',
    textTransform: 'capitalize',
  },
  
  featureDescription: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
  },
  
  featureBenefits: {
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    paddingTop: 12,
  },
  
  benefitsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 8,
  },
  
  benefitsList: {
    flexDirection: 'column',
  },
  
  benefitItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  
  benefitText: {
    fontSize: 12,
    color: '#6B7280',
    marginLeft: 8,
    flex: 1,
  },
  
  // Analytics styles
  analyticsSection: {
    backgroundColor: '#FFFFFF',
    margin: 16,
    padding: 20,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
    marginBottom: 16,
  },
  
  usageMetrics: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  
  usageItem: {
    width: '48%',
    marginBottom: 12,
  },
  
  usageLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 4,
  },
  
  usageValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
  },
  
  toolPerformance: {
    flexDirection: 'column',
  },
  
  performanceItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  
  performanceInfo: {
    flex: 1,
  },
  
  performanceName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  performanceCategory: {
    fontSize: 12,
    color: '#6B7280',
    textTransform: 'capitalize',
  },
  
  performanceMetrics: {
    alignItems: 'flex-end',
  },
  
  performanceMetric: {
    fontSize: 11,
    color: '#6B7280',
    marginBottom: 2,
  },
  
  performanceInsights: {
    flexDirection: 'column',
  },
  
  insightItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  
  insightText: {
    fontSize: 14,
    color: '#6B7280',
    marginLeft: 12,
    flex: 1,
  },
  
  // Compatibility styles
  compatibilitySection: {
    backgroundColor: '#FFFFFF',
    margin: 16,
    padding: 20,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  
  platformList: {
    flexDirection: 'column',
  },
  
  platformCard: {
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  },
  
  platformHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  
  platformInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  
  platformDetails: {
    marginLeft: 12,
    flex: 1,
  },
  
  platformName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  platformVersion: {
    fontSize: 12,
    color: '#6B7280',
  },
  
  platformStats: {
    alignItems: 'flex-end',
  },
  
  platformUsers: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  platformCompatibility: {
    fontSize: 12,
    color: '#6B7280',
  },
  
  platformDevices: {
    marginBottom: 12,
  },
  
  devicesTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 4,
  },
  
  deviceText: {
    fontSize: 11,
    color: '#6B7280',
    marginBottom: 2,
  },
  
  compatibilityBar: {
    height: 4,
    backgroundColor: '#E5E7EB',
    borderRadius: 2,
    overflow: 'hidden',
  },
  
  compatibilityFill: {
    height: '100%',
    borderRadius: 2,
  },
  
  requirementsList: {
    flexDirection: 'column',
  },
  
  requirementItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  
  requirementInfo: {
    marginLeft: 12,
    flex: 1,
  },
  
  requirementTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  requirementValue: {
    fontSize: 12,
    color: '#6B7280',
    lineHeight: 16,
  },
  
  downloadInfo: {
    flexDirection: 'column',
  },
  
  downloadItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  
  downloadText: {
    fontSize: 14,
    color: '#6B7280',
    marginLeft: 12,
    flex: 1,
  },
  
  // Modal styles
  modalContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
    flex: 1,
  },
  
  modalCloseButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#F3F4F6',
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  modalContent: {
    flex: 1,
    padding: 16,
  },
  
  modalFooter: {
    flexDirection: 'row',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
  },
  
  modalButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 8,
    marginHorizontal: 4,
  },
  
  // Tool preview styles
  toolPreview: {
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    padding: 16,
  },
  
  toolPreviewHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  
  toolPreviewInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  
  toolPreviewTitleInfo: {
    flex: 1,
  },
  
  toolPreviewTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
  },
  
  toolPreviewVersion: {
    fontSize: 14,
    color: '#6B7280',
  },
  
  toolPreviewMeta: {
    alignItems: 'flex-end',
  },
  
  downloadCount: {
    fontSize: 12,
    color: '#6B7280',
  },
  
  toolPreviewSection: {
    marginBottom: 16,
  },
  
  toolPreviewSectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 8,
  },
  
  toolPreviewText: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
  },
  
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  
  featureItemText: {
    fontSize: 14,
    color: '#1F2937',
    marginLeft: 8,
    flex: 1,
  },
  
  appInfo: {
    flexDirection: 'column',
  },
  
  appInfoItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 6,
    padding: 8,
    marginBottom: 4,
  },
  
  appInfoLabel: {
    fontSize: 14,
    color: '#1F2937',
  },
  
  appInfoValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6B7280',
  },
  
  permissionsList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  
  permissionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 8,
    marginBottom: 8,
  },
  
  permissionText: {
    fontSize: 12,
    color: '#1F2937',
    marginLeft: 4,
  },
});
